// Copyright 2022 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license. See LICENSE.md in the
// project root for license information.
import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  DataField,
  DataFieldType,
  DataOption,
  SystemPermission,
} from 'src/app/generated/blueprint.api';
import { PermissionDataService } from 'src/app/data/permission/permission-data.service';
import { Theme } from '@cmusei/crucible-common';
import { MselPlus } from 'src/app/data/msel/msel-data.service';
import { MselQuery } from 'src/app/data/msel/msel.query';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DataFieldDataService } from 'src/app/data/data-field/data-field-data.service';
import { DataFieldQuery } from 'src/app/data/data-field/data-field.query';
import { DataFieldTemplateQuery } from 'src/app/data/data-field/data-field-template.query';
import { DataFieldEditDialogComponent } from '../data-field-edit-dialog/data-field-edit-dialog.component';
import { CompetencyOptionsDialogComponent } from '../competency-options-dialog/competency-options-dialog.component';
import { DataOptionEditDialogComponent } from '../data-option-edit-dialog/data-option-edit-dialog.component';
import { DataOptionListDialogComponent } from '../data-option-list-dialog/data-option-list-dialog.component';
import { DataOptionImportDialogComponent } from '../data-option-import-dialog/data-option-import-dialog.component';
import { ItemDownloadDialogComponent } from '../item-download-dialog/item-download-dialog.component';
import { InjectTypeDataService } from 'src/app/data/inject-type/inject-type-data.service';
import { InjectTypeQuery } from 'src/app/data/inject-type/inject-type.query';
import { MselDataService } from 'src/app/data/msel/msel-data.service';
import { MatDialog } from '@angular/material/dialog';
import { CrucibleDialogService } from '@cmusei/crucible-common';
import { UIDataService } from 'src/app/data/ui/ui-data.service';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-data-field-list',
  templateUrl: './data-field-list.component.html',
  styleUrls: ['./data-field-list.component.scss'],
  standalone: false
})
export class DataFieldListComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input() loggedInUserId: string;
  @Input() showTemplates: boolean;
  @Input() userTheme: Theme;
  @Input() injectTypeId: string;
  msel = new MselPlus();
  allDataFields: DataField[] = [];
  dataFieldList: DataField[] = [];
  templateList: DataField[] = [];
  changedDataField: DataField = {};
  filteredDataFieldList: DataField[] = [];
  filterControl = new UntypedFormControl();
  filterString = '';
  sort: Sort = { active: '', direction: '' };
  allowDragAndDrop = true;
  dataFieldTypes = DataFieldType;
  systemDefinedDataFields = new Array<DataField>();
  dataFieldDataSource = new MatTableDataSource<DataField>(
    new Array<DataField>()
  );
  templateDataSource = new MatTableDataSource<DataField>(
    new Array<DataField>()
  );
  readonly baseSystemColumns: string[] = ['draghandleSpacer', 'action'];
  readonly baseColumns: string[] = ['draghandle', 'action'];
  readonly trailingColumns: string[] = ['name', 'datatype', 'options'];
  systemFieldDisplayedColumns: string[] = [
    ...this.baseSystemColumns,
    ...this.trailingColumns,
  ];
  displayedColumns: string[] = [
    ...this.baseColumns,
    ...this.trailingColumns,
  ];
  readonly displayOptions: { key: string; column: string; label: string; title: string; hideInAdmin?: boolean; hideInInjectType?: boolean }[] = [
    { key: 'onScenarioEventList', column: 'events', label: 'List', title: 'Display on Scenario Events list', hideInAdmin: true, hideInInjectType: true },
    { key: 'onExerciseView', column: 'exercise', label: 'View', title: 'Display on the Exercise View', hideInAdmin: true },
    { key: 'isAssessorVisible', column: 'assessor', label: 'Assess', title: 'Display on the Assessor View' },
    { key: 'isInformationField', column: 'information', label: 'Info', title: 'Display for Information Events' },
    { key: 'isFacilitationField', column: 'facilitation', label: 'Facil', title: 'Display for Facilitation Events' },
    { key: 'isShownOnDefaultTab', column: 'default', label: 'Default', title: 'Display on the default tab of the scenario event edit dialog' },
    { key: 'isOnlyShownToOwners', column: 'devs', label: 'Devs', title: 'Hide this field from participants on all views - show only to content developers and MSEL owners' },
  ];
  readonly defaultVisibleDisplayColumns: string[] = ['onScenarioEventList', 'onExerciseView'];
  visibleDisplayColumns: string[] = [];
  private unsubscribe$ = new Subject();
  // context menu
  @ViewChild(MatMenuTrigger, { static: true }) contextMenu: MatMenuTrigger;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('jsonInput') jsonInput: ElementRef<HTMLInputElement>;
  contextMenuPosition = { x: '0px', y: '0px' };

  constructor(
    private mselQuery: MselQuery,
    private dataFieldDataService: DataFieldDataService,
    private dataFieldQuery: DataFieldQuery,
    private dataFieldTemplateQuery: DataFieldTemplateQuery,
    private injectTypeDataService: InjectTypeDataService,
    private injectTypeQuery: InjectTypeQuery,
    private mselDataService: MselDataService,
    public dialog: MatDialog,
    public dialogService: CrucibleDialogService,
    private permissionDataService: PermissionDataService,
    private uiDataService: UIDataService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.msel.id = null;
    // subscribe to filter string changes
    this.filterControl.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((term) => {
        this.filterString = term;
        this.sortChanged(this.sort);
      });
    // load the dataField templates
    this.dataFieldDataService.loadTemplates();
    // subscribe to the templates
    this.dataFieldTemplateQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (templates) => {
          this.templateList = templates;
          // if on the templates page, the dataFieldList is the templates
          if (this.showTemplates) {
            this.dataFieldList = templates;
          }
          this.sortChanged(this.sort);
        },
        (error) => {
          this.templateDataSource.data = [];
        }
      );
  }

  ngOnInit() {
    // Load permissions and trigger change detection when loaded
    this.permissionDataService.load()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
    const saved = this.uiDataService.getVisibleDataFieldColumns();
    const seed = saved && saved.length > 0 ? saved : this.defaultVisibleDisplayColumns;
    const availableKeys = this.availableDisplayOptions().map((o) => o.key);
    this.visibleDisplayColumns = seed.filter((k) => availableKeys.includes(k));
    this.applyVisibleDisplayColumns();
    if (this.showTemplates) {
      this.msel = new MselPlus();
      this.mselDataService.setActive('');
    } else {
      // subscribe to data fields
      this.dataFieldQuery
        .selectAll()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((dataFields) => {
          this.allDataFields = dataFields;
          this.setDataFieldList();
        });
      if (this.injectTypeId) {
        this.msel = new MselPlus();
        this.mselDataService.setActive('');
        // load data fields for the inject type
        this.dataFieldDataService.loadByInjectType(this.injectTypeId);
      } else {
        // subscribe to the active MSEL changes to get future changes
        (this.mselQuery.selectActive() as Observable<MselPlus>)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((m) => {
            Object.assign(this.msel, m);
            this.setDataFieldList();
            if (this.msel && this.msel.id) {
              if (
                this.msel.useGallery &&
                !this.displayedColumns.includes('integration')
              ) {
                this.displayedColumns.push('integration');
              } else if (
                !this.msel.useGallery &&
                this.displayedColumns.includes('integration')
              ) {
                this.displayedColumns.splice(this.displayedColumns.length - 1);
              }
              this.applyVisibleDisplayColumns();
              this.createSystemDefinedDataFields();
            }
            this.sortChanged(this.sort);
          });
      }
    }
  }

  ngAfterViewInit() {
    this.dataFieldDataSource.paginator = this.paginator;
  }

  setDataFieldList() {
    this.dataFieldList = [];
    this.allDataFields.forEach((df) => {
      if (
        (df.mselId && df.mselId === this.msel.id) ||
        (df.injectTypeId && df.injectTypeId === this.injectTypeId)
      ) {
        this.dataFieldList.push({ ...df });
      }
    });
    this.sortChanged(this.sort);
  }

  createSystemDefinedDataFields() {
    this.systemDefinedDataFields = [
      {
        displayOrder: -4,
        name: 'Move',
        onScenarioEventList: this.msel.showMoveOnScenarioEventList,
        onExerciseView: this.msel.showMoveOnExerciseView,
        isAssessorVisible: this.msel.showMoveOnAssessorView,
        isInformationField: false,
        isFacilitationField: false,
        galleryArticleParameter: 'Gallery Move',
        dataType: 'Move',
        description: 'System defined',
      },
      {
        displayOrder: -3,
        name: 'Group',
        onScenarioEventList: this.msel.showGroupOnScenarioEventList,
        onExerciseView: this.msel.showGroupOnExerciseView,
        isAssessorVisible: this.msel.showGroupOnAssessorView,
        isInformationField: false,
        isFacilitationField: false,
        galleryArticleParameter: 'Gallery Inject',
        dataType: 'Integer',
        description: 'System defined',
      },
      {
        displayOrder: -2,
        name: 'Execution Time',
        onScenarioEventList: this.msel.showTimeOnScenarioEventList,
        onExerciseView: this.msel.showTimeOnExerciseView,
        isAssessorVisible: this.msel.showTimeOnAssessorView,
        isInformationField: false,
        isFacilitationField: false,
        galleryArticleParameter: '- - -',
        dataType: 'DateTime',
        description: 'System defined',
      },
      {
        displayOrder: -1,
        name: 'Integration Target',
        onScenarioEventList: this.msel.showIntegrationTargetOnScenarioEventList,
        onExerciseView: this.msel.showIntegrationTargetOnExerciseView,
        isAssessorVisible: this.msel.showIntegrationTargetOnAssessorView,
        isInformationField: false,
        isFacilitationField: false,
        galleryArticleParameter: '- - -',
        dataType: 'IntegrationTarget' as any,
        description: 'System defined',
      },
    ];
  }

  addOrEditDataField(dataFieldIn: DataField, makeTemplate: boolean) {
    let dialogTitle = 'Add a Data Field';
    const dataOptions =
      dataFieldIn && dataFieldIn.isChosenFromList
        ? dataFieldIn.dataOptions.slice()
        : [];
    let dataField = dataFieldIn ? { ...dataFieldIn } : null;
    if (!dataField) {
      dataField = {
        mselId: this.msel.id,
        injectTypeId: this.injectTypeId,
        displayOrder: this.dataFieldList.length + 1,
        isShownOnDefaultTab: true,
        onScenarioEventList: true,
        onExerciseView: true,
        isTemplate: makeTemplate,
      } as DataField;
    } else {
      if (makeTemplate === dataField.isTemplate) {
        dialogTitle = dialogTitle.replace('Add a', 'Edit');
      } else {
        dataField.id = null;
        if (makeTemplate) {
          dialogTitle = dialogTitle.replace('Add', 'Make');
        }
      }
      if (makeTemplate) {
        dataField.mselId = null;
        dataField.injectTypeId = null;
      } else {
        dataField.mselId = this.msel.id;
        dataField.injectTypeId = this.injectTypeId;
        dataField.displayOrder = dataField.isTemplate
          ? this.dataFieldList.length + 1
          : dataField.displayOrder;
      }
      dataField.isTemplate = makeTemplate;
      dataField.onScenarioEventList = true;
      dataField.onExerciseView = true;
    }
    if (dataField.isTemplate) {
      dialogTitle += ' Template';
    }
    dataField.dataOptions = dataOptions;
    const dialogRef = this.dialog.open(DataFieldEditDialogComponent, {
      minWidth: '650px',
      maxWidth: '90vw',
      width: 'auto',
      data: {
        dataField: dataField,
        canEdit: this.canEdit(),
        isOwner: this.msel.hasRole(this.loggedInUserId, null).owner,
        galleryArticleParameters: this.getUnusedGalleryOptions(
          dataField.galleryArticleParameter
        ),
        useGallery: this.msel.useGallery,
        useCite: this.msel.useCite,
        dataFieldTypes: this.dataFieldTypes,
        title: dialogTitle,
        onSave: (df: DataField) => this.saveDataField(df)
      },
    });
    dialogRef.componentInstance.editComplete.subscribe((result) => {
      if (result.saveChanges && result.dataField) {
        const dataFieldId = this.saveDataField(result.dataField);
      }
      dialogRef.close();
    });
  }

  saveDataField(dataField: DataField): string {
    if (dataField.id) {
      this.dataFieldDataService.updateDataField(dataField);
    } else {
      dataField.id = uuidv4();
      this.dataFieldDataService.add(dataField);
    }
    return dataField.id;
  }

  selectFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    this.dataFieldDataService.uploadJson(file, 'events', true);
    this.jsonInput.nativeElement.value = null;
  }

  openDownloadDialog() {
    const items = (this.templateDataSource.data || []).map((d) => ({
      id: d.id,
      label: d.name || d.id,
    }));
    const dialogRef = this.dialog.open(ItemDownloadDialogComponent, {
      maxWidth: '90vw',
      width: 'auto',
      data: {
        title: 'Select Data Field templates to download',
        items,
      },
    });
    dialogRef.afterClosed().subscribe((selectedIds: string[] | undefined) => {
      if (selectedIds && selectedIds.length > 0) {
        this.downloadJsonItems(selectedIds);
      }
    });
  }

  downloadJsonItems(ids: string[]) {
    this.dataFieldDataService.downloadJson(ids).subscribe(
      (data) => {
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = 'data-field-templates.json';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      (err) => {
        window.alert('Error downloading file');
      }
    );
  }

  deleteDataField(dataField: DataField): void {
    this.dialogService
      .confirm({ title: 'Delete Data Field', message: 'Are you sure that you want to delete ' + dataField.name + '?'
       }).afterClosed().subscribe((result) => {
        if (result) {
          this.dataFieldDataService.delete(dataField.id);
        }
      });
  }

  sortChanged(sort: Sort) {
    this.sort = sort;
    if (
      (this.sort && this.sort.active && this.sort.direction) ||
      this.filterString
    ) {
      this.allowDragAndDrop = false;
    } else {
      this.allowDragAndDrop = !this.showTemplates;
    }
    const filteredData = this.getFilteredDataFields(
      this.msel.id,
      this.injectTypeId,
      this.dataFieldList
    );
    this.dataFieldDataSource.data = filteredData;
    if (this.paginator) {
      this.paginator.length = filteredData.length;
    }
    this.templateDataSource.data = this.templateList.sort((a, b) =>
      this.sortDataFields(a, b)
    );
  }

  private getNumOrDefault(value: number): number {
    if (typeof value !== undefined && typeof value !== null) {
      return +value;
    }
    return Number.NEGATIVE_INFINITY;
  }

  sortDataFieldsByDefinedSort(sort: Sort, a: DataField, b: DataField): number {
    let sortResult = 0;
    const isAsc = 'asc' === sort.direction;
    const dir = isAsc ? 1 : -1;
    const sortFieldName = sort.active;
    switch (sortFieldName) {
      case 'displayOrder':
        const numValA = this.getNumOrDefault(a.displayOrder);
        const numValB = this.getNumOrDefault(b.displayOrder);
        if (+numValA < +numValB) {
          sortResult = -1;
        } else if (+numValA > +numValB) {
          sortResult = 1;
        }
        break;
      case 'onScenarioEventList':
      case 'onExerciseView':
      case 'isInformationField':
      case 'isFacilitationField':
      case 'isAssessorVisible':
      case 'isShownOnDefaultTab':
      case 'isOnlyShownToOwners':
        const aChecked = a[sortFieldName];
        const bChecked = b[sortFieldName];
        if (aChecked !== bChecked) {
          sortResult = aChecked ? -1 : 1;
        }
        break;
      case 'options':
        const aOptionsCount = a.dataOptions?.length || 0;
        const bOptionsCount = b.dataOptions?.length || 0;
        if (aOptionsCount < bOptionsCount) {
          sortResult = -1;
        } else if (aOptionsCount > bOptionsCount) {
          sortResult = 1;
        }
        break;
      case 'name':
      case 'dataType':
      case 'galleryArticleParameter':
        const aStr = a[sortFieldName];
        const bStr = b[sortFieldName];
        sortResult = Intl.Collator().compare(
          aStr ? aStr.trim().toLowerCase() : '',
          bStr ? bStr.trim().toLowerCase() : ''
        );
        break;
    }
    if (sortResult) {
      sortResult = sortResult * dir;
    }
    return sortResult;
  }

  sortDataFields(a: DataField, b: DataField): number {
    const defaultSort: Sort = this.showTemplates
      ? { active: 'name', direction: 'asc' }
      : { active: 'displayOrder', direction: 'asc' };
    let sortResult = 0;
    if (this.sort && this.sort.active && this.sort.direction) {
      sortResult = this.sortDataFieldsByDefinedSort(this.sort, a, b);
    }
    if (!sortResult) {
      sortResult = this.sortDataFieldsByDefinedSort(defaultSort, a, b);
    }
    return sortResult;
  }

  getFilteredDataFields(
    mselId: string,
    injectTypeId: string,
    dataFields: DataField[]
  ): DataField[] {
    let filteredDataFields: DataField[] = [];
    if (dataFields && dataFields.length > 0) {
      dataFields.forEach(m => {
        filteredDataFields.push({ ...m });
      });
      if (this.filterString) {
        const filterString = this.filterString?.toLowerCase();
        filteredDataFields = filteredDataFields.filter((a) =>
          a.name?.toLowerCase().includes(filterString)
        );
      }
    }
    if (filteredDataFields) {
      filteredDataFields.sort((a, b) => this.sortDataFields(a, b));
    }

    const returnArray =
      this.showTemplates || (!mselId && !injectTypeId)
        ? filteredDataFields
        : this.systemDefinedDataFields.concat(filteredDataFields);
    return returnArray;
  }

  getDataOptionsString(dataField: DataField): string {
    if (dataField.dataOptions && dataField.dataOptions.length > 0) {
      const count = dataField.dataOptions.length;
      if (dataField.dataType === DataFieldType.Competency) {
        return `${count} competenc${count !== 1 ? 'ies' : 'y'}`;
      }
      return `${count} option${count !== 1 ? 's' : ''}`;
    } else if (dataField.dataType === DataFieldType.Competency) {
      return 'Manage';
    } else {
      return ' ';
    }
  }

  viewDataOptions(dataField: DataField, event: Event) {
    event.stopPropagation();
    if (dataField.dataType === DataFieldType.Competency || (dataField.dataOptions && dataField.dataOptions.length > 0)) {
      const hasEditPermission = this.canEdit() || this.msel.hasRole(this.loggedInUserId, null).owner;
      const supportsOptionList =
        dataField.dataType === DataFieldType.Double ||
        dataField.dataType === DataFieldType.Integer ||
        dataField.dataType === DataFieldType.String ||
        dataField.dataType === DataFieldType.Competency;
      const canAddOptions = hasEditPermission && supportsOptionList;

      if (dataField.dataType === DataFieldType.Competency) {
        const compDialogRef = this.dialog.open(CompetencyOptionsDialogComponent, {
          width: '900px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          data: {
            dataFieldId: dataField.id,
            dataOptions: dataField.dataOptions,
            canEdit: canAddOptions
          }
        });
        compDialogRef.afterClosed().subscribe((updatedOptions) => {
          const updated = { ...dataField, dataOptions: updatedOptions || dataField.dataOptions };
          this.dataFieldDataService.updateDataField(updated);
        });
      } else {
        this.dialog.open(DataOptionListDialogComponent, {
          width: '900px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          data: {
            dataOptions: dataField.dataOptions,
            canEdit: canAddOptions,
            onEdit: (option: DataOption) => this.editDataOption(option, dataField),
            onDelete: (option: DataOption) => this.deleteDataOption(option, dataField),
            onAdd: () => this.addDataOption(dataField),
            onImport: () => this.importDataOptions(dataField)
          }
        });
      }
    }
  }

  addDataOption(dataField: DataField) {
    const dataOption: DataOption = {
      displayOrder: dataField.dataOptions.length + 1,
      dataFieldId: dataField.id
    };
    this.openDataOptionEditDialog(dataOption, dataField);
  }

  editDataOption(option: DataOption, dataField: DataField) {
    const selected = { ...option };
    this.openDataOptionEditDialog(selected, dataField);
  }

  deleteDataOption(option: DataOption, dataField: DataField) {
    const index = dataField.dataOptions.findIndex(x => x.id === option.id);
    if (index >= 0) {
      dataField.dataOptions.splice(index, 1);
      this.dataFieldDataService.updateDataField(dataField);
    }
  }

  private openDataOptionEditDialog(dataOption: DataOption, dataField: DataField) {
    const dialogRef = this.dialog.open(DataOptionEditDialogComponent, {
      minWidth: '400px',
      maxWidth: '90vw',
      width: 'auto',
      data: { dataOption }
    });
    dialogRef.componentInstance.editComplete.subscribe((result) => {
      if (result.saveChanges && result.dataOption) {
        if (dataOption.id) {
          const index = dataField.dataOptions.findIndex(x => x.id === dataOption.id);
          dataField.dataOptions[index] = dataOption;
        } else {
          dataOption.id = uuidv4();
          dataField.dataOptions.push(dataOption);
        }
        this.dataFieldDataService.updateDataField(dataField);
      }
      dialogRef.close();
    });
  }

  importDataOptions(dataField: DataField) {
    const dialogRef = this.dialog.open(DataOptionImportDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: {
        dataFieldId: dataField.id,
        existingOptions: dataField.dataOptions
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && Array.isArray(result)) {
        dataField.dataOptions.push(...result);
        this.dataFieldDataService.updateDataField(dataField);
      }
    });
  }

  availableDisplayOptions() {
    return this.displayOptions.filter((opt) => {
      if (this.showTemplates && opt.hideInAdmin) return false;
      if (this.injectTypeId && opt.hideInInjectType) return false;
      return true;
    });
  }

  onVisibleColumnsChange(selectedKeys: string[]) {
    this.visibleDisplayColumns = selectedKeys;
    this.uiDataService.setVisibleDataFieldColumns(selectedKeys);
    this.applyVisibleDisplayColumns();
  }

  private applyVisibleDisplayColumns() {
    const available = this.availableDisplayOptions();
    const chosenColumns = available
      .filter((opt) => this.visibleDisplayColumns.includes(opt.key))
      .map((opt) => opt.column);
    const hasIntegration = this.displayedColumns.includes('integration');
    const trailing = hasIntegration
      ? [...this.trailingColumns, 'integration']
      : [...this.trailingColumns];
    this.displayedColumns = [...this.baseColumns, ...chosenColumns, ...trailing];
    this.systemFieldDisplayedColumns = [...this.baseSystemColumns, ...chosenColumns, ...this.trailingColumns];
  }

  saveChange(dataField: DataField) {
    if (dataField.displayOrder > 0) {
      this.dataFieldDataService.updateDataField(dataField);
    } else {
      switch (dataField.name) {
        case 'Move':
          this.msel.showMoveOnExerciseView = dataField.onExerciseView;
          this.msel.showMoveOnScenarioEventList = dataField.onScenarioEventList;
          this.msel.showMoveOnAssessorView = dataField.isAssessorVisible;
          break;
        case 'Group':
          this.msel.showGroupOnExerciseView = dataField.onExerciseView;
          this.msel.showGroupOnScenarioEventList =
            dataField.onScenarioEventList;
          this.msel.showGroupOnAssessorView = dataField.isAssessorVisible;
          break;
        case 'Execution Time':
          this.msel.showTimeOnExerciseView = dataField.onExerciseView;
          this.msel.showTimeOnScenarioEventList = dataField.onScenarioEventList;
          this.msel.showTimeOnAssessorView = dataField.isAssessorVisible;
          break;
        case 'Integration Target':
          this.msel.showIntegrationTargetOnExerciseView = dataField.onExerciseView;
          this.msel.showIntegrationTargetOnScenarioEventList = dataField.onScenarioEventList;
          this.msel.showIntegrationTargetOnAssessorView = dataField.isAssessorVisible;
          break;
      }
      this.saveMsel();
    }
  }

  galleryToDo(): string {
    let todoString = '';
    let todoList = Object.assign([], this.msel.galleryArticleParameters);
    if (todoList && todoList.length > 0) {
      todoList = todoList.filter(
        (x) =>
          !this.dataFieldList.some((df) => df.galleryArticleParameter === x)
      );
      todoString = todoList.join(', ');
    }
    return todoString;
  }

  getUnusedGalleryOptions(parameter: string): string[] {
    const options = [];
    if (this.msel.galleryArticleParameters) {
      this.msel.galleryArticleParameters.forEach((gap) => {
        if (
          parameter === gap ||
          !this.dataFieldList.some((df) => df.galleryArticleParameter === gap)
        ) {
          options.push(gap);
        }
      });
    }
    return options;
  }

  compareIntegrationValues(a: any, b: any) {
    if ((!a && !b) || a === b) {
      return true;
    } else {
      return false;
    }
  }

  fieldIsSystemDefined(index: number, rowData: DataField) {
    return rowData.displayOrder < 0;
  }

  saveMsel() {
    this.mselDataService.updateMsel(this.msel);
  }

  dropHandler(event: CdkDragDrop<string[]>) {
    // NOTE: The event index only considers rows that were draggable starting at 0
    // The nonSystemDefaultFieldStartIndex tracks which actual index is equivalent to zero
    const nonSystemDefaultFieldStartIndex =
      this.dataFieldDataSource.data.findIndex(
        (df, index) => !this.fieldIsSystemDefined(index, df)
      );
    const prevIndex = event.previousIndex + nonSystemDefaultFieldStartIndex;
    const currIndex = event.currentIndex + nonSystemDefaultFieldStartIndex;
    // sanity check
    if (nonSystemDefaultFieldStartIndex >= 0 && prevIndex !== currIndex) {
      const droppedField = event.item.data;
      const targetField = this.dataFieldDataSource.data[currIndex];
      this.saveDataField({
        ...droppedField,
        displayOrder: targetField.displayOrder,
      });
    }
  }

  canEdit(): boolean {
    if (this.showTemplates) {
      return this.permissionDataService.hasPermission(SystemPermission.ManageDataFields);
    } else {
      return this.permissionDataService.hasPermission(SystemPermission.EditMsels) ||
        this.msel.hasRole(this.loggedInUserId, '').editor;
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
